import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * W16-F2-S1 — Tenant context.
 *
 * Holds the user's currently-selected partner id. The Entra JWT covers
 * every accessible partner by design (RBAC scopes resolved server-side
 * by Core); the active partner is a UI concern that decides which slice
 * of data the app shows.
 *
 * Persistence: localStorage key `senviro.activePartnerId` so the
 * selection survives reloads. Selecting a partner invalidates scoped
 * react-query namespaces (callers do this via the consumer hook in
 * `lib/api/partners.ts`).
 */

const STORAGE_KEY = 'senviro.activePartnerId';

export interface TenantContextValue {
  activePartnerId: string | null;
  setActivePartner: (id: string | null) => void;
}

const TenantContext = createContext<TenantContextValue | null>(null);

function readStored(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(id: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (id == null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, id);
  } catch {
    // localStorage may be unavailable (private mode) — silently degrade.
  }
}

export function TenantProvider({ children }: { children: ReactNode }): JSX.Element {
  const [activePartnerId, setStateId] = useState<string | null>(readStored);

  // Re-sync if another tab updates the active partner.
  useEffect(() => {
    function onStorage(event: StorageEvent): void {
      if (event.key === STORAGE_KEY) {
        setStateId(event.newValue);
      }
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const setActivePartner = useCallback((id: string | null) => {
    writeStored(id);
    setStateId(id);
  }, []);

  const value = useMemo<TenantContextValue>(
    () => ({ activePartnerId, setActivePartner }),
    [activePartnerId, setActivePartner],
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) {
    throw new Error('useTenant() must be used inside <TenantProvider>');
  }
  return ctx;
}

export const TENANT_STORAGE_KEY = STORAGE_KEY;
